import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Download } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import {
    compositeText, getBrandGuides, OverlayState, CompositeTextResponse, apiClient, BrandGuide, resolveOverlayFont, BUNDLED_OVERLAY_FONTS,
    AdImageRatioKey, AdImageVariant, RatioGeometry, AD_ASPECT_RATIOS, DERIVED_AD_RATIOS,
    putAdImageVariant, cropAdImageVariant,
} from '../../services/api';
import PanCropViewport from './PanCropViewport';
import { panCropBox, panAxisFor, cropWindowSize } from './adImageCropMath';
import './TextOverlayModal.css';

// File-local helper — same pattern as SocialPostEditModal.tsx, CampaignDetail.tsx.
// Not exported from any shared module; each component declares its own copy.
const getApiError = (err: any, fallback: string): string =>
    err?.response?.data?.detail || err?.message || fallback;

// ── Position grid labels for aria-label ──────────────────────────────────────
const POSITION_LABELS: Record<number, string> = {
    0: 'Top left',
    1: 'Top centre',
    2: 'Top right',
    3: 'Middle left',
    4: 'Centre',
    5: 'Middle right',
    6: 'Bottom left',
    7: 'Bottom centre',
    8: 'Bottom right',
};

// ── Default overlay state (used when initialOverlayState is null/undefined) ──
const DEFAULT_OVERLAY: OverlayState = {
    text: '',
    size: 'medium',
    text_color: '#FFFFFF',
    glow_enabled: false,
    glow_color: '#000000',
    shadow_enabled: false,
    shadow_color: '#000000',
    position: 4,           // centre
    font_family: 'inter',
    italic: false,
    fallback_to_prompt: false,
    logo_shadow_enabled: false,
    logo_shadow_color: '#000000',
    logo_glow_enabled: false,
    logo_glow_color: '#000000',
    // base_image_url intentionally omitted — frontend stamps it on first composite
};

// ── Hex validation ─────────────────────────────────────────────────────────────
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function sanitiseHex(value: string, fallback: string): string {
    return HEX_RE.test(value) ? value : fallback;
}

// ── Colour preset type ─────────────────────────────────────────────────────────
type ColorPreset = 'white' | 'black' | 'brand_primary' | 'brand_secondary' | 'custom';

function detectPreset(hex: string, primary: string | null, secondary: string | null): ColorPreset {
    const h = hex.toUpperCase();
    if (h === '#FFFFFF') return 'white';
    if (h === '#000000') return 'black';
    if (primary && h === primary.toUpperCase()) return 'brand_primary';
    if (secondary && h === secondary.toUpperCase()) return 'brand_secondary';
    return 'custom';
}

// ── Logo variant picker ────────────────────────────────────────────────────────
type LogoVariantKey =
    | 'logo_url'
    | 'logo_dark_bg_url'
    | 'logo_monochrome_black_url'
    | 'logo_monochrome_white_url'
    | 'favicon_url';

interface LogoVariant {
    key: LogoVariantKey;
    label: string;
    url: string;  // non-null at point of render
}

// Preference order — used for default selection (first non-null wins)
// and for dropdown render order so the UI is stable.
const LOGO_VARIANT_ORDER: { key: LogoVariantKey; label: string }[] = [
    { key: 'logo_url',                  label: 'Primary' },
    { key: 'logo_dark_bg_url',          label: 'Dark Background' },
    { key: 'logo_monochrome_black_url', label: 'Monochrome Black' },
    { key: 'logo_monochrome_white_url', label: 'Monochrome White' },
    { key: 'favicon_url',               label: 'Icon / Favicon' },
];

// Type-safe helper to retrieve a logo URL by variant key from a BrandGuide.
// Using a switch instead of `guide[key as keyof BrandGuide] as string` ensures
// TypeScript will surface a compile error if any of the five BrandGuide fields
// are renamed in the future.
const getBrandLogoUrl = (guide: BrandGuide, key: LogoVariantKey): string | null => {
    const val = guide[key];
    return typeof val === 'string' && val ? val : null;
};

// ── Font registration via FontFace API ─────────────────────────────────────────
// We register fonts programmatically (not via <style>@font-face) because Canvas2D
// only uses fonts that are present in document.fonts AND loaded. The FontFace API
// gives us a Promise we can await before painting, removing the fallback-flash on
// font-family change. URLs use the /overlay-fonts/ mount (main.py).
interface OverlayFontSpec { family: string; weight: string; style: string; url: string; format: 'opentype' | 'truetype' | 'woff2' | 'woff'; }

// Slugify a brand family name for the synthetic FontFace family — MUST match the
// backend overlay_font_resolver.slugify_family so the canvas family string lines up
// with the cached file. e.g. "Merriweather Sans" -> "merriweather-sans".
const brandFontSlug = (family: string): string =>
    family.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'font';
// Display-name aliases of the 6 bundled faces — skip these as "brand" options
// (they already have a built-in <option>).
const BUNDLED_FAMILY_ALIASES = new Set(['inter', 'playfair display', 'montserrat', 'bebas neue', 'poppins', 'caveat']);
const OVERLAY_FONT_SPECS: OverlayFontSpec[] = [
    { family: 'AIME-Inter',           weight: 'bold', style: 'normal', url: '/overlay-fonts/Inter-Bold.otf',                 format: 'opentype' },
    { family: 'AIME-Inter',           weight: 'bold', style: 'italic', url: '/overlay-fonts/Inter-BoldItalic.otf',           format: 'opentype' },
    { family: 'AIME-PlayfairDisplay', weight: 'bold', style: 'normal', url: '/overlay-fonts/PlayfairDisplay-Bold.ttf',       format: 'truetype' },
    { family: 'AIME-PlayfairDisplay', weight: 'bold', style: 'italic', url: '/overlay-fonts/PlayfairDisplay-BoldItalic.ttf', format: 'truetype' },
    { family: 'AIME-Montserrat',      weight: 'bold', style: 'normal', url: '/overlay-fonts/Montserrat-Bold.ttf',            format: 'truetype' },
    { family: 'AIME-Montserrat',      weight: 'bold', style: 'italic', url: '/overlay-fonts/Montserrat-BoldItalic.ttf',      format: 'truetype' },
    // Bebas Neue ships Regular only (it's already a heavy display face).
    // Register as weight=bold so `ctx.font = 'bold ...'` finds it; register
    // italic variant to the same file so toggling italic produces faux-italic
    // rather than a sans-serif fallback (backend _resolve_font does the same).
    { family: 'AIME-BebasNeue',       weight: 'bold', style: 'normal', url: '/overlay-fonts/BebasNeue-Regular.ttf',           format: 'truetype' },
    { family: 'AIME-BebasNeue',       weight: 'bold', style: 'italic', url: '/overlay-fonts/BebasNeue-Regular.ttf',           format: 'truetype' },
    { family: 'AIME-Poppins',         weight: 'bold', style: 'normal', url: '/overlay-fonts/Poppins-Bold.ttf',                format: 'truetype' },
    { family: 'AIME-Poppins',         weight: 'bold', style: 'italic', url: '/overlay-fonts/Poppins-BoldItalic.ttf',          format: 'truetype' },
    // Caveat is a script — no italic variant exists, register both styles to
    // the same bold file so italic toggle still resolves to Caveat (faux).
    { family: 'AIME-Caveat',          weight: 'bold', style: 'normal', url: '/overlay-fonts/Caveat-Bold.ttf',                 format: 'truetype' },
    { family: 'AIME-Caveat',          weight: 'bold', style: 'italic', url: '/overlay-fonts/Caveat-Bold.ttf',                 format: 'truetype' },
];

// Module-level singleton — load each font once per page, return the Promise so
// multiple modal opens share the same in-flight load. Map key = family|weight|style.
const overlayFontPromises = new Map<string, Promise<void>>();
function ensureOverlayFontLoaded(spec: OverlayFontSpec): Promise<void> {
    const key = `${spec.family}|${spec.weight}|${spec.style}`;
    const cached = overlayFontPromises.get(key);
    if (cached) return cached;
    if (typeof document === 'undefined' || !('fonts' in document) || typeof (window as any).FontFace !== 'function') {
        return Promise.resolve();
    }
    const promise = (async () => {
        try {
            const face = new FontFace(spec.family, `url(${spec.url}) format('${spec.format}')`, {
                weight: spec.weight,
                style: spec.style,
            });
            const loaded = await face.load();
            (document as any).fonts.add(loaded);
            // Pre-warm Canvas2D's font cache for the exact CSS shorthand we'll set.
            // Chrome/Firefox sometimes won't paint a freshly-added FontFace on canvas
            // until the font set has been queried with the matching shorthand.
            try {
                await (document as any).fonts.load(`${spec.style === 'italic' ? 'italic ' : ''}${spec.weight} 16px "${spec.family}"`);
            } catch { /* non-fatal */ }
        } catch (err) {
            // Surface failures during dev — silent fallback to sans-serif was masking
            // the original bug where font swap had no visible effect on the canvas.
            // eslint-disable-next-line no-console
            console.error('[TextOverlayModal] font load failed', spec, err);
        }
    })();
    overlayFontPromises.set(key, promise);
    return promise;
}

// ── Props ──────────────────────────────────────────────────────────────────────

export interface TextOverlayModalProps {
    baseImageUrl: string;
    initialOverlayState?: OverlayState | null;
    fallbackVisualPrompt?: string;
    projectId?: string;
    onApplied: (response: CompositeTextResponse) => void;
    onClose: () => void;
    /** Opt-in: show the ratio strip and per-ratio pan/layout editing.
     *  Omitted/false -> the component behaves EXACTLY as it does today.
     *  Only the Ad Copy saved-ad preview modal passes true. */
    aspectVariantsEnabled?: boolean;
    savedAdId?: number;          // required when aspectVariantsEnabled
    initialVariants?: AdImageVariant[] | null;
    /** Server-computed geometry (GET .../image-variants' `ratios`), whose
     *  target_w/target_h honour any AIME_AD_VARIANT_*_W/H env override.
     *  Falls back to the hardcoded AD_ASPECT_RATIOS client constant when
     *  absent (e.g. the initial fetch hasn't resolved yet), which is
     *  correct for the common case (no override set) but would silently
     *  diverge from the backend if one ever is -- using this when present
     *  avoids that. */
    ratioGeometry?: RatioGeometry[] | null;
    onVariantsSaved?: (variants: AdImageVariant[]) => void;
}

// ── Per-ratio layout slot — the fields that swap when the ratio tab changes ─
interface RatioLayoutSlot {
    position: number;
    size: OverlayState['size'];
    logoPosition: number;
    logoSize: 'small' | 'medium' | 'large' | 'xlarge';
    panX: number;
    panY: number;
}

// ── Component ──────────────────────────────────────────────────────────────────

const TextOverlayModal: React.FC<TextOverlayModalProps> = ({
    baseImageUrl,
    initialOverlayState,
    fallbackVisualPrompt,
    projectId,
    onApplied,
    onClose,
    aspectVariantsEnabled,
    savedAdId,
    initialVariants,
    ratioGeometry,
    onVariantsSaved,
}) => {
    // Server geometry, matched by ratio, when available -- see the prop's
    // doc comment. Recomputed on every render (cheap array scan over at
    // most 3 items); no need for useMemo.
    const targetDimsFor = useCallback((ratio: AdImageRatioKey): { width: number; height: number } => {
        const server = ratioGeometry?.find(g => g.ratio === ratio);
        if (server) return { width: server.target_w, height: server.target_h };
        return { width: AD_ASPECT_RATIOS[ratio].width, height: AD_ASPECT_RATIOS[ratio].height };
    }, [ratioGeometry]);

    // Initialise form from persisted state or defaults
    const init = initialOverlayState ?? DEFAULT_OVERLAY;
    const [text, setText] = useState(init.text);
    const [size, setSize] = useState<OverlayState['size']>(init.size);
    const [glowEnabled, setGlowEnabled] = useState(init.glow_enabled);
    const [shadowEnabled, setShadowEnabled] = useState(init.shadow_enabled);
    const [position, setPosition] = useState(init.position);
    const [fallbackToPrompt, setFallbackToPrompt] = useState(init.fallback_to_prompt);
    const [fallbackPromptText, setFallbackPromptText] = useState(fallbackVisualPrompt ?? '');

    // ── Aspect variants (ratio strip) state — only meaningful when
    //    aspectVariantsEnabled. Seeded from the 1:1's initial layout so the
    //    user isn't restyling from scratch, and from initialVariants (an
    //    ad's already-configured ratios, if any) when present. ────────────
    const seedRatioLayout = (v: AdImageVariant | undefined): RatioLayoutSlot => ({
        position: v?.overlay_state?.position ?? init.position,
        size: v?.overlay_state?.size ?? init.size,
        logoPosition: v?.overlay_state?.logo_position ?? init.logo_position ?? 8,
        logoSize: v?.overlay_state?.logo_size ?? init.logo_size ?? 'medium',
        panX: v?.pan_x ?? 0.5,
        panY: v?.pan_y ?? 0.5,
    });
    const initialVariantsRef = useRef(initialVariants);
    const [ratioLayouts, setRatioLayouts] = useState<Record<AdImageRatioKey, RatioLayoutSlot>>(() => ({
        square: { position: init.position, size: init.size, logoPosition: init.logo_position ?? 8, logoSize: init.logo_size ?? 'medium', panX: 0.5, panY: 0.5 },
        vertical: seedRatioLayout(initialVariantsRef.current?.find(v => v.ratio === 'vertical')),
        horizontal: seedRatioLayout(initialVariantsRef.current?.find(v => v.ratio === 'horizontal')),
    }));
    const [activeRatio, setActiveRatio] = useState<AdImageRatioKey>('square');
    // The two derived ratios' persisted server state (crop_url/variant_image_url/
    // etc.) — updated as "Generate All Ratios" progresses. Not used for layout
    // (that's ratioLayouts); this is what onVariantsSaved reports up.
    const [variantResults, setVariantResults] = useState<Partial<Record<'vertical' | 'horizontal', AdImageVariant>>>(() => {
        const out: Partial<Record<'vertical' | 'horizontal', AdImageVariant>> = {};
        for (const v of initialVariantsRef.current ?? []) {
            if (v.ratio === 'vertical' || v.ratio === 'horizontal') out[v.ratio] = v;
        }
        return out;
    });
    const [generatingStep, setGeneratingStep] = useState<string | null>(null);
    const [ratioErrors, setRatioErrors] = useState<Partial<Record<AdImageRatioKey, string>>>({});
    // Set true the moment the user drags/nudges a pan (handlePanChange) —
    // guards the re-seed effect below from clobbering an in-progress edit.
    // Deliberately NOT set by handleRatioTabChange: merely switching tabs to
    // look at a ratio isn't a "deliberate edit" worth protecting against a
    // late initialVariants re-seed.
    const userTouchedLayoutRef = useRef(false);
    // initialVariants is captured by ref (above) only for the ONE-TIME
    // lazy state initializer, which by React's own contract can't see a
    // later prop update — if the modal opens before AdCopyModule's
    // getAdImageVariants() fetch resolves, initialVariants arrives as `[]`
    // at mount and only becomes populated on a LATER render. Re-seed once,
    // reactively, the first time it actually contains data, but never once
    // the user has started configuring a ratio themselves.
    useEffect(() => {
        if (!initialVariants || initialVariants.length === 0) return;
        if (userTouchedLayoutRef.current) return;
        setRatioLayouts(prev => ({
            ...prev,
            vertical: seedRatioLayout(initialVariants.find(v => v.ratio === 'vertical')),
            horizontal: seedRatioLayout(initialVariants.find(v => v.ratio === 'horizontal')),
        }));
        setVariantResults(prev => {
            const out = { ...prev };
            for (const v of initialVariants) {
                if (v.ratio === 'vertical' || v.ratio === 'horizontal') out[v.ratio] = v;
            }
            return out;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialVariants]);
    // Real natural dimensions of the loaded base image — needed for the
    // client-side crop-preview math (PanCropViewport) and the ratio-aware
    // canvas render below. Set once the base <img> loads.
    const [baseNaturalSize, setBaseNaturalSize] = useState<{ w: number; h: number } | null>(null);

    // Font and italic state
    const [fontFamily, setFontFamily] = useState<string>(init.font_family ?? 'inter');
    const [italic, setItalic] = useState<boolean>(init.italic ?? false);
    // Inline note when a brand font can't be served for overlays (fallback to Inter).
    const [brandFontNote, setBrandFontNote] = useState<string | null>(null);

    // Brand colours
    const [brandPrimary, setBrandPrimary] = useState<string | null>(null);
    const [brandSecondary, setBrandSecondary] = useState<string | null>(null);

    // Full brand guide — used by the logo variant picker in the Logo tab.
    const [brandGuide, setBrandGuide] = useState<BrandGuide | null>(null);

    // Text colour state — raw hex (used for the colour picker in custom mode)
    const [textColorHex, setTextColorHex] = useState(init.text_color);
    const [textColorPreset, setTextColorPreset] = useState<ColorPreset>('white'); // refined after brand load

    // Glow colour state
    const [glowColorHex, setGlowColorHex] = useState(init.glow_color);
    const [glowColorPreset, setGlowColorPreset] = useState<ColorPreset>('black'); // refined after brand load

    // Shadow colour state
    const [shadowColorHex, setShadowColorHex] = useState(init.shadow_color ?? '#000000');
    const [shadowColorPreset, setShadowColorPreset] = useState<ColorPreset>('black'); // refined after brand load

    // Derived resolved colour values
    const resolveColor = useCallback((preset: ColorPreset, custom: string): string => {
        if (preset === 'white') return '#FFFFFF';
        if (preset === 'black') return '#000000';
        if (preset === 'brand_primary') return brandPrimary || '#FFFFFF';
        if (preset === 'brand_secondary') return brandSecondary || '#FFFFFF';
        return custom;
    }, [brandPrimary, brandSecondary]);

    const textColor = resolveColor(textColorPreset, textColorHex);
    const glowColor = resolveColor(glowColorPreset, glowColorHex);
    const shadowColor = resolveColor(shadowColorPreset, shadowColorHex);

    const [previewUrl] = useState(baseImageUrl);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Canvas refs
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const baseImgElRef = useRef<HTMLImageElement | null>(null);
    const logoImgRef = useRef<HTMLImageElement | null>(null);
    const debounceRef = useRef<number | null>(null);

    // Track overlay mousedown target to prevent drag-dismiss
    const overlayMouseDownTarget = useRef<EventTarget | null>(null);

    // ── Logo state ────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<'text' | 'logo'>('text');
    // Seed logoSource from persisted state so re-edit restores the correct source radio button.
    // State type matches the wire format ('brand_guide' | 'upload') exactly — no translation needed
    // at submit time and no mismatch against the backend's OverlayStateSchema.logo_source field.
    const [logoSource, setLogoSource] = useState<'brand_guide' | 'upload'>(
        initialOverlayState?.logo_source === 'upload' ? 'upload' : 'brand_guide'
    );
    // Two separate URL slots so switching the source radio never shows the wrong image.
    // brandLogoUrl — set by the brand-guide fetch effect (below); never written by the upload handler.
    // uploadedLogoUrl — set by handleLogoUpload; never overwritten by the brand-guide fetch.
    const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(
        // On re-edit with brand_guide source, seed the display URL from the persisted snapshot
        // so the preview shows immediately before the async fetch completes.
        initialOverlayState?.logo_source !== 'upload' ? (initialOverlayState?.logo_url ?? null) : null
    );
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(
        // On re-edit with upload source, seed the uploaded URL from the persisted snapshot.
        initialOverlayState?.logo_source === 'upload' ? (initialOverlayState?.logo_url ?? null) : null
    );
    const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large' | 'xlarge'>(
        initialOverlayState?.logo_size ?? 'medium'
    );
    const [logoPosition, setLogoPosition] = useState<number>(initialOverlayState?.logo_position ?? 8);
    const [logoOpacity, setLogoOpacity] = useState<number>(initialOverlayState?.logo_opacity ?? 1.0);
    const [logoEnabled, setLogoEnabled] = useState<boolean>(initialOverlayState?.logo_enabled ?? false);

    // Logo effect state — the `??` is load-bearing for legacy overlay_state (Known Risks (a))
    const [logoShadowEnabled, setLogoShadowEnabled] = useState<boolean>(initialOverlayState?.logo_shadow_enabled ?? false);
    const [logoShadowColorHex, setLogoShadowColorHex] = useState(initialOverlayState?.logo_shadow_color ?? '#000000');
    const [logoShadowColorPreset, setLogoShadowColorPreset] = useState<ColorPreset>('black'); // refined after brand load
    const [logoGlowEnabled, setLogoGlowEnabled] = useState<boolean>(initialOverlayState?.logo_glow_enabled ?? false);
    const [logoGlowColorHex, setLogoGlowColorHex] = useState(initialOverlayState?.logo_glow_color ?? '#000000');
    const [logoGlowColorPreset, setLogoGlowColorPreset] = useState<ColorPreset>('black'); // refined after brand load

    const logoShadowColor = resolveColor(logoShadowColorPreset, logoShadowColorHex);
    const logoGlowColor   = resolveColor(logoGlowColorPreset,   logoGlowColorHex);

    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [logoWarning, setLogoWarning] = useState<string | null>(null);
    const [logoLoadError, setLogoLoadError] = useState<string | null>(null);
    const logoFileRef = useRef<HTMLInputElement>(null);

    // Resolved logo URL — used by the canvas and sent to the backend.
    // Picks from the correct slot based on logoSource so switching source radio
    // never causes the wrong image to be shown or submitted.
    const resolvedLogoUrl = useMemo<string | null>(() => {
        if (!logoEnabled) return null;
        return logoSource === 'brand_guide' ? brandLogoUrl : uploadedLogoUrl;
    }, [logoEnabled, logoSource, brandLogoUrl, uploadedLogoUrl]);

    // SVG detection — canvas cannot paint cross-origin SVGs reliably; skip in preview.
    // Must be declared after resolvedLogoUrl.
    const isSvgLogo = !!resolvedLogoUrl && /\.svg($|\?)/i.test(resolvedLogoUrl);

    // ── Logo variant picker state ─────────────────────────────────────────────
    // Variants that are actually populated on the active brand guide.
    // Memoised on brandGuide so the dropdown options are stable.
    const availableLogoVariants = useMemo<LogoVariant[]>(() => {
        if (!brandGuide) return [];
        return LOGO_VARIANT_ORDER
            .map(({ key, label }) => {
                const url = getBrandLogoUrl(brandGuide, key);
                return url ? { key, label, url } : null;
            })
            .filter((v): v is LogoVariant => v !== null);
    }, [brandGuide]);

    // Selected variant key within the brand_guide source.
    const [selectedLogoVariant, setSelectedLogoVariant] = useState<LogoVariantKey | null>(null);

    // On mount (and when availableLogoVariants changes): detect which variant
    // matches the persisted logo_url on re-edit, or fall back to first available.
    // Captured once at mount. Safe because call sites unmount/remount per asset,
    // so the ref always reflects the current asset's state.
    const initialOverlayStateRef = useRef(initialOverlayState);
    useEffect(() => {
        if (availableLogoVariants.length === 0) {
            setSelectedLogoVariant(null);
            return;
        }
        // Re-edit detection: match persisted brand-guide logo URL to a variant.
        const persisted = initialOverlayStateRef.current?.logo_url ?? null;
        if (persisted && initialOverlayStateRef.current?.logo_source !== 'upload') {
            const match = availableLogoVariants.find(v => v.url === persisted);
            if (match) {
                setSelectedLogoVariant(match.key);
                return;
            }
        }
        // First open OR persisted URL no longer matches any variant — pick first available.
        setSelectedLogoVariant(availableLogoVariants[0].key);
    }, [availableLogoVariants]);

    // When the selected variant changes, update brandLogoUrl so the canvas preview
    // and the submit payload both see the correct URL.
    useEffect(() => {
        if (!selectedLogoVariant) return;
        const match = availableLogoVariants.find(v => v.key === selectedLogoVariant);
        if (match) setBrandLogoUrl(match.url);
    }, [selectedLogoVariant, availableLogoVariants]);

    // ── Fetch brand colours on mount ─────────────────────────────────────────
    useEffect(() => {
        if (!projectId) return;
        let cancelled = false;
        (async () => {
            try {
                const guides = await getBrandGuides(projectId, 'active');
                if (cancelled) return;
                const guide = guides?.[0];
                if (guide) {
                    // Store the full guide so the Logo tab variant picker can access all URLs.
                    setBrandGuide(guide);
                    if (guide.primary_color) setBrandPrimary(guide.primary_color);
                    if (guide.secondary_color) setBrandSecondary(guide.secondary_color);
                    // Populate brand logo URL for the picker — but only on first open.
                    // On re-edit the persisted logo_url is already seeded from state
                    // initialisation above; the variant detection effect (below) will
                    // restore the correct variant URL once availableLogoVariants resolves.
                    // On first open (no persisted logo_url): seed with the primary logo
                    // so the preview shows immediately when the user enables the logo toggle.
                    if (guide.logo_url && !initialOverlayState?.logo_url) {
                        setBrandLogoUrl(guide.logo_url);
                    }
                }
            } catch {
                // Non-fatal — Brand Primary/Secondary options are simply hidden.
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // ── Re-detect colour presets after brand colours land ────────────────────
    useEffect(() => {
        setTextColorPreset(detectPreset(init.text_color, brandPrimary, brandSecondary));
        setGlowColorPreset(detectPreset(init.glow_color, brandPrimary, brandSecondary));
        setShadowColorPreset(detectPreset(init.shadow_color ?? '#000000', brandPrimary, brandSecondary));
        setLogoShadowColorPreset(detectPreset(init.logo_shadow_color ?? '#000000', brandPrimary, brandSecondary));
        setLogoGlowColorPreset(detectPreset(init.logo_glow_color ?? '#000000', brandPrimary, brandSecondary));
        // Run once on mount + once when brand colours arrive (async fetch).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandPrimary, brandSecondary]);

    // ── Brand font <option>s — the guide's primary/secondary fonts, when they're
    //    not already one of the 6 bundled built-ins (those have their own option). ──
    const brandFontOptions = useMemo(() => {
        const opts: { value: string; label: string }[] = [];
        const seen = new Set<string>();
        for (const fam of [brandGuide?.primary_font, brandGuide?.secondary_font]) {
            const name = (fam || '').trim();
            const key = name.toLowerCase();
            if (!name || seen.has(key)) continue;
            if (BUNDLED_OVERLAY_FONTS.includes(key as any) || BUNDLED_FAMILY_ALIASES.has(key)) continue;
            seen.add(key);
            opts.push({ value: `brand:${name}`, label: `${name} (Brand)` });
        }
        return opts;
    }, [brandGuide]);

    // ── Escape key to close ──────────────────────────────────────────────────
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isSubmitting) {
                onClose();
            }
        },
        [isSubmitting, onClose],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // ── Overlay click-to-close (with mousedown-target guard) ────────────────
    const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        overlayMouseDownTarget.current = e.target;
    };

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isSubmitting) return;
        if (
            e.target === e.currentTarget &&
            overlayMouseDownTarget.current === e.currentTarget
        ) {
            onClose();
        }
    };

    // ── Colour preset handlers ────────────────────────────────────────────────
    const handleTextPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const p = e.target.value as ColorPreset;
        setTextColorPreset(p);
        if (p === 'white') setTextColorHex('#FFFFFF');
        else if (p === 'black') setTextColorHex('#000000');
        else if (p === 'brand_primary' && brandPrimary) setTextColorHex(brandPrimary);
        else if (p === 'brand_secondary' && brandSecondary) setTextColorHex(brandSecondary);
        // 'custom' — leave hex as-is, picker stays visible
    };

    const handleGlowPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const p = e.target.value as ColorPreset;
        setGlowColorPreset(p);
        if (p === 'white') setGlowColorHex('#FFFFFF');
        else if (p === 'black') setGlowColorHex('#000000');
        else if (p === 'brand_primary' && brandPrimary) setGlowColorHex(brandPrimary);
        else if (p === 'brand_secondary' && brandSecondary) setGlowColorHex(brandSecondary);
        // 'custom' — leave hex as-is, picker stays visible
    };

    const handleShadowPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const p = e.target.value as ColorPreset;
        setShadowColorPreset(p);
        if (p === 'white') setShadowColorHex('#FFFFFF');
        else if (p === 'black') setShadowColorHex('#000000');
        else if (p === 'brand_primary' && brandPrimary) setShadowColorHex(brandPrimary);
        else if (p === 'brand_secondary' && brandSecondary) setShadowColorHex(brandSecondary);
        // 'custom' — leave hex as-is, picker stays visible
    };

    const handleLogoShadowPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const p = e.target.value as ColorPreset;
        setLogoShadowColorPreset(p);
        if (p === 'white') setLogoShadowColorHex('#FFFFFF');
        else if (p === 'black') setLogoShadowColorHex('#000000');
        else if (p === 'brand_primary' && brandPrimary) setLogoShadowColorHex(brandPrimary);
        else if (p === 'brand_secondary' && brandSecondary) setLogoShadowColorHex(brandSecondary);
        // 'custom' — leave hex as-is, picker stays visible
    };

    const handleLogoGlowPresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const p = e.target.value as ColorPreset;
        setLogoGlowColorPreset(p);
        if (p === 'white') setLogoGlowColorHex('#FFFFFF');
        else if (p === 'black') setLogoGlowColorHex('#000000');
        else if (p === 'brand_primary' && brandPrimary) setLogoGlowColorHex(brandPrimary);
        else if (p === 'brand_secondary' && brandSecondary) setLogoGlowColorHex(brandSecondary);
        // 'custom' — leave hex as-is, picker stays visible
    };

    // ── Logo upload handler ───────────────────────────────────────────────────
    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Belt-and-braces SVG rejection: check both MIME type and extension because
        // some OS file-association tables report 'image/svg' (without '+xml') or 'text/xml'.
        if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
            // Write to logoLoadError (scoped to the logo field), not the submit error banner.
            setLogoLoadError('SVG logos are not supported. Please use PNG or JPG.');
            e.target.value = '';
            return;
        }
        setIsUploadingLogo(true);
        setLogoLoadError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            // Use apiClient (axios) — the request interceptor injects the correct
            // Authorization: Bearer <access_token> header automatically. Do NOT set
            // Content-Type manually — axios/browser must supply the multipart boundary.
            const resp = await apiClient.post('/brand-guides/upload-asset', formData);
            const url = resp.data?.url || resp.data?.asset_url || resp.data?.file_url || '';
            if (!url) throw new Error('No URL returned');
            // Write only to the upload slot — never touch brandLogoUrl.
            setUploadedLogoUrl(url);
            setLogoSource('upload');
        } catch (err: any) {
            setLogoLoadError(getApiError(err, 'Logo upload failed.'));
        } finally {
            setIsUploadingLogo(false);
            if (logoFileRef.current) logoFileRef.current.value = '';
        }
    };

    // ── Ratio tab change — swaps ONLY the per-ratio layout fields (position,
    //    size, logo position/size); every shared field (text, font, colours,
    //    logo identity) stays exactly as-is. Pan lives entirely in
    //    ratioLayouts (no "live" variable elsewhere to swap). ────────────────
    const handleRatioTabChange = useCallback((nextRatio: AdImageRatioKey) => {
        if (nextRatio === activeRatio) return;
        setRatioLayouts(prev => ({
            ...prev,
            [activeRatio]: { ...prev[activeRatio], position, size, logoPosition, logoSize },
        }));
        const next = ratioLayouts[nextRatio];
        setPosition(next.position);
        setSize(next.size);
        setLogoPosition(next.logoPosition);
        setLogoSize(next.logoSize);
        setActiveRatio(nextRatio);
    }, [activeRatio, position, size, logoPosition, logoSize, ratioLayouts]);

    const handlePanChange = useCallback((ratio: AdImageRatioKey, panX: number, panY: number) => {
        userTouchedLayoutRef.current = true;
        setRatioLayouts(prev => ({ ...prev, [ratio]: { ...prev[ratio], panX, panY } }));
    }, []);

    // ── Canvas render (mirrors PIL logic) — synchronous, uses pre-loaded refs ─
    const renderCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const img = baseImgElRef.current;
        if (!canvas || !img) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Ratio-aware: for a derived ratio (9:16 / 1.91:1), the canvas's
        // LOGICAL size is the ratio's TARGET dimensions (matching what PIL
        // produces after crop+resize, and what composite-text sizes text
        // against on the real crop_url file) — not the base image's natural
        // size. The source rect is the crop window in the base image's own
        // pixel space; drawImage's 9-arg form scales it to fill the canvas,
        // mirroring the server's crop-then-resize pipeline exactly. Square
        // (or aspectVariantsEnabled=false) keeps today's behaviour verbatim:
        // full natural-size canvas, no crop.
        const isDerivedPreview = aspectVariantsEnabled && activeRatio !== 'square' && baseNaturalSize;
        let w: number, h: number;
        if (isDerivedPreview && baseNaturalSize) {
            const dims = targetDimsFor(activeRatio);
            w = dims.width;
            h = dims.height;
        } else {
            w = img.naturalWidth;
            h = img.naturalHeight;
        }
        canvas.width = w;
        canvas.height = h;
        if (isDerivedPreview) {
            // The canvas is nested INSIDE PanCropViewport's frame as its
            // `children` overlay (see PanCropViewport.tsx) — that frame is
            // already sized correctly for this exact ratio (and is
            // ResizeObserver-responsive), so filling it via CSS % is both
            // simpler and safer than an independent max-width/max-height
            // guess: a fixed pixel cap here previously overflowed its
            // container on any layout narrower than the cap (the .pan-crop-
            // overlay CSS class carries width:100%/height:100%). Scoped to
            // isDerivedPreview only — the four other TextOverlayModal
            // consumers, and the Square/1:1 tab itself (rendered standalone,
            // not inside PanCropViewport), keep today's exact width:100%/
            // maxWidth:480px sizing below.
            canvas.style.maxWidth = '';
            canvas.style.maxHeight = '';
            canvas.style.width = '100%';
            canvas.style.height = '100%';
        } else {
            // CSS-side: clamp display width to 480px while preserving aspect ratio.
            canvas.style.maxWidth = '480px';
            canvas.style.maxHeight = '';
            canvas.style.width = '100%';
            canvas.style.height = 'auto';
        }

        ctx.clearRect(0, 0, w, h);
        if (isDerivedPreview && baseNaturalSize) {
            const layout = ratioLayouts[activeRatio];
            const [left, top, right, bottom] = panCropBox(
                baseNaturalSize.w, baseNaturalSize.h, w, h, layout.panX, layout.panY,
            );
            ctx.drawImage(img, left, top, right - left, bottom - top, 0, 0, w, h);
        } else {
            ctx.drawImage(img, 0, 0);
        }

        // Hoist safe-zone insets — used by both logo and text render blocks.
        const SAFE = 0.08;
        const padX = Math.floor(w * SAFE);
        const padY = Math.floor(h * SAFE);

        // ── Logo layer — draw BEFORE text so text sits on top ─────────────
        // SVG logos are intentionally hidden here (browser canvas security model
        // treats cross-origin SVGs as tainted). Still sent to the backend.
        if (logoEnabled && resolvedLogoUrl && !isSvgLogo) {
            const logoImg = logoImgRef.current;
            if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
                // Mirrors image_compositor.py's LOGO_SIZE_RATIO — must stay in sync.
                const LOGO_SIZE_RATIO: Record<string, number> = { small: 0.10, medium: 0.18, large: 0.28, xlarge: 0.35 };
                const targetW = Math.max(1, Math.floor(w * (LOGO_SIZE_RATIO[logoSize] ?? 0.18)));
                const scale = targetW / logoImg.naturalWidth;
                const targetH = Math.max(1, Math.floor(logoImg.naturalHeight * scale));
                const lcol = logoPosition % 3;
                const lrow = Math.floor(logoPosition / 3);
                const lx =
                    lcol === 0 ? padX :
                    lcol === 1 ? Math.floor((w - targetW) / 2) :
                                 w - targetW - padX;
                const ly =
                    lrow === 0 ? padY :
                    lrow === 1 ? Math.floor((h - targetH) / 2) :
                                 h - targetH - padY;
                // Logo shadow/glow — native canvas shadow silhouettes off the
                // drawn image's alpha channel with zero manual code. Effects are
                // approximate by nature (Gaussian blur is a resampling filter, no
                // pixel-exact cross-renderer guarantee expected), so native canvas
                // shadow is the right call here (unlike the TEXT case, which
                // hand-rolls offsets for pixel-fidelity with PIL's hard-edged draws).
                // Naive drawImage x3 (shadow/glow/crisp) would triple-composite the
                // logo's own pixels — invisible at opacity=1 but visibly wrong at
                // 0.75/0.5 — so each fx pass draws the source fully OFF-canvas and
                // uses shadowOffsetX to push only the shadow back into view.
                const hexToRgba = (hex: string, a: number): string => {
                    const r = parseInt(hex.slice(1, 3), 16);
                    const g = parseInt(hex.slice(3, 5), 16);
                    const b = parseInt(hex.slice(5, 7), 16);
                    return `rgba(${r}, ${g}, ${b}, ${a})`;
                };
                const clampedOpacity = Math.max(0, Math.min(1, logoOpacity));
                // Tightened 2026-08-29, 0.03 -> 0.01 -> 0.005 — must mirror
                // image_compositor.py's _LOGO_SHADOW_OFFSET_RATIO or the preview
                // no longer matches the output.
                const LOGO_SHADOW_OFFSET_RATIO = 0.005, LOGO_GLOW_BLUR_RATIO = 0.06, LOGO_FX_MIN_PX = 2;
                const LOGO_SHADOW_ALPHA = 180 / 255, LOGO_GLOW_ALPHA = 200 / 255;

                const drawFxPass = (rgba: string, blurPx: number, offX: number, offY: number) => {
                    ctx.save();
                    ctx.globalAlpha = clampedOpacity;      // effects dim with the logo — matches PIL
                    ctx.shadowColor = rgba;
                    ctx.shadowBlur = blurPx;
                    ctx.shadowOffsetX = offX + w;          // push the shadow back onto the canvas
                    ctx.shadowOffsetY = offY;
                    ctx.drawImage(logoImg, lx - w, ly, targetW, targetH);   // source is fully off-canvas
                    ctx.restore();
                };

                if (logoShadowEnabled) {
                    const d = Math.max(LOGO_FX_MIN_PX, Math.round(targetW * LOGO_SHADOW_OFFSET_RATIO));
                    drawFxPass(hexToRgba(logoShadowColor, LOGO_SHADOW_ALPHA), 0, d, d);
                }
                if (logoGlowEnabled) {
                    const r = Math.max(LOGO_FX_MIN_PX, Math.round(targetW * LOGO_GLOW_BLUR_RATIO));
                    drawFxPass(hexToRgba(logoGlowColor, LOGO_GLOW_ALPHA), 2 * r, 0, 0);   // ← 2× (spec σ = blur/2)
                }

                ctx.save();
                ctx.globalAlpha = Math.max(0, Math.min(1, logoOpacity));
                ctx.drawImage(logoImg, lx, ly, targetW, targetH);
                ctx.restore();
            }
        }

        if (!text.trim()) return;

        // Mirror PIL constants (SAFE, padX, padY already hoisted above for logo block)
        const SIZE_RATIO: Record<string, number> = { small: 0.04, medium: 0.07, large: 0.11 };
        const GLOW_OFFSETS = [[-3,0],[3,0],[0,-3],[0,3],[-2,-2],[2,-2],[-2,2],[2,2]];
        const GLOW_ALPHA   = 160 / 255;   // 0.627 — matches PIL _GLOW_ALPHA
        const SHADOW_ALPHA = 180 / 255;   // 0.706 — matches PIL _SHADOW_ALPHA
        const SHADOW_DX = 3, SHADOW_DY = 3;
        const MAX_WRAP_LINES = 6;

        const fontPx = Math.max(12, Math.floor(h * (SIZE_RATIO[size] ?? 0.07)));
        const italicCss = italic ? 'italic ' : '';
        // Font family map — values must match the FontFace `family` strings
        // registered in OVERLAY_FONT_SPECS.
        // fontFamily is now free-text (string), so the lookup MUST have an explicit
        // default — an unrecognised/brand value must never yield undefined (which would
        // silently paint sans-serif on canvas).
        const cssFamily =
            fontFamily.startsWith('brand:') ? `"AIME-Brand-${brandFontSlug(fontFamily.slice('brand:'.length))}"` :
            fontFamily === 'inter'      ? '"AIME-Inter"' :
            fontFamily === 'playfair'   ? '"AIME-PlayfairDisplay"' :
            fontFamily === 'montserrat' ? '"AIME-Montserrat"' :
            fontFamily === 'bebas'      ? '"AIME-BebasNeue"' :
            fontFamily === 'poppins'    ? '"AIME-Poppins"' :
            fontFamily === 'caveat'     ? '"AIME-Caveat"' :
                                           '"AIME-Inter"';  // default (IMP-4)
        ctx.font = `${italicCss}bold ${fontPx}px ${cssFamily}, sans-serif`;
        ctx.textBaseline = 'top';

        // padX / padY already declared above — reuse them here.
        const maxWidth = w - 2 * padX;

        // Greedy word-wrap — same algorithm as backend wrap_text().
        const wrap = (input: string): string[] => {
            if (!input.trim()) return [];
            const out: string[] = [];
            for (const para of input.split('\n')) {
                const words = para.split(/\s+/).filter(Boolean);
                if (!words.length) { out.push(''); continue; }
                let cur = words[0];
                for (let i = 1; i < words.length; i++) {
                    const cand = `${cur} ${words[i]}`;
                    if (ctx.measureText(cand).width <= maxWidth) cur = cand;
                    else { out.push(cur); cur = words[i]; if (out.length >= MAX_WRAP_LINES) break; }
                }
                if (out.length < MAX_WRAP_LINES) out.push(cur);
                if (out.length >= MAX_WRAP_LINES) break;
            }
            return out.slice(0, MAX_WRAP_LINES);
        };

        const lines = wrap(text);
        if (!lines.length) return;

        const metrics = ctx.measureText('Mg');
        // 1.15 line-height multiplier matches PIL
        const lineHeight = Math.floor((metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent) * 1.15);
        const blockH = lineHeight * lines.length;
        const lineWidths = lines.map(l => Math.floor(ctx.measureText(l).width));
        const blockW = Math.max(...lineWidths, 0);

        const col = position % 3;
        const row = Math.floor(position / 3);
        const xAnchor =
            col === 0 ? padX :
            col === 1 ? Math.floor((w - blockW) / 2) :
                        w - blockW - padX;
        const yAnchor =
            row === 0 ? padY :
            row === 1 ? Math.floor((h - blockH) / 2) :
                        h - blockH - padY;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const tw = lineWidths[i];
            const x =
                col === 0 ? xAnchor :
                col === 1 ? xAnchor + Math.floor((blockW - tw) / 2) :
                            xAnchor + (blockW - tw);
            const y = yAnchor + i * lineHeight;

            // Drop shadow
            if (shadowEnabled) {
                ctx.globalAlpha = SHADOW_ALPHA;
                ctx.fillStyle = shadowColor;
                ctx.fillText(line, x + SHADOW_DX, y + SHADOW_DY);
            }
            // Glow
            if (glowEnabled) {
                ctx.globalAlpha = GLOW_ALPHA;
                ctx.fillStyle = glowColor;
                for (const [dx, dy] of GLOW_OFFSETS) ctx.fillText(line, x + dx, y + dy);
            }
            // Main text — reset globalAlpha before drawing even if only shadow drew,
            // to avoid rendering at SHADOW_ALPHA opacity when glow is off.
            ctx.globalAlpha = 1;
            ctx.fillStyle = textColor;
            ctx.fillText(line, x, y);
        }
        // Final defensive reset in case future code adds more semi-transparent layers.
        ctx.globalAlpha = 1;
    }, [text, size, textColor, glowEnabled, glowColor, shadowEnabled, shadowColor, position, fontFamily, italic, logoEnabled, resolvedLogoUrl, isSvgLogo, logoSize, logoPosition, logoOpacity, logoShadowEnabled, logoShadowColor, logoGlowEnabled, logoGlowColor, aspectVariantsEnabled, activeRatio, baseNaturalSize, ratioLayouts, targetDimsFor]);

    // ── Load base image once (uses original URL for re-edit integrity) ────────
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';  // remote /assets/ images; Pexels sends ACAO: *
        img.onload  = () => {
            baseImgElRef.current = img;
            setBaseNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
            renderCanvas();
        };
        img.onerror = () => {
            // Can't load image (e.g. CORS from some provider) — show placeholder
            baseImgElRef.current = null;
            const canvas = canvasRef.current;
            if (!canvas) return;
            canvas.width = 480;
            canvas.height = 300;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.fillStyle = 'var(--panel-strong, #888)';
            ctx.fillRect(0, 0, 480, 300);
            ctx.fillStyle = '#aaa';
            ctx.font = '14px sans-serif';
            ctx.fillText('Preview unavailable', 10, 20);
            console.warn('[TextOverlayModal] Could not load base image for canvas preview:', img.src);
        };
        // For re-edit: use the original (matches what we composite against in handleSubmit).
        img.src = initialOverlayState?.base_image_url ?? baseImageUrl;
    }, [baseImageUrl, initialOverlayState?.base_image_url, renderCanvas]);

    // ── Load logo image into ref when resolvedLogoUrl changes ────────────────
    // This keeps renderCanvas synchronous — no await inside the render loop.
    // SVG logos are intentionally skipped (canvas security model / tainted canvas).
    useEffect(() => {
        if (!logoEnabled || !resolvedLogoUrl || isSvgLogo) {
            logoImgRef.current = null;
            renderCanvas();
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { logoImgRef.current = img; renderCanvas(); };
        img.onerror = () => {
            logoImgRef.current = null;
            renderCanvas();
        };
        img.src = resolvedLogoUrl;
    }, [logoEnabled, resolvedLogoUrl, isSvgLogo, renderCanvas]);

    // ── Debounced re-render on any form state change ──────────────────────────
    useEffect(() => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(renderCanvas, 16);
        return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
    }, [renderCanvas]);

    // ── Ensure overlay font is loaded before canvas paints ────────────────────
    // Canvas2D silently substitutes sans-serif if the requested family isn't in
    // document.fonts yet. We register via FontFace API (more reliable than
    // <style>@font-face for canvas use) and re-render once the load resolves.
    useEffect(() => {
        let cancelled = false;
        const style = italic ? 'italic' : 'normal';

        // Brand font (Tier 2): resolve to a same-origin web font, load it, then re-render.
        if (fontFamily.startsWith('brand:')) {
            const bare = fontFamily.slice('brand:'.length);
            (async () => {
                try {
                    const res = await resolveOverlayFont(bare, italic, projectId);
                    if (cancelled) return;
                    if (res.bundled_key) {
                        // Brand font IS one of the bundled faces — switch to its key.
                        setBrandFontNote(null);
                        setFontFamily(res.bundled_key);
                        return;
                    }
                    // Falsy/garbage guard: an unresolvable brand font MUST fall back to a
                    // valid bundled key, never leave the canvas pointing at an unloaded
                    // AIME-Brand-* family (which Canvas2D silently paints as sans-serif).
                    if (!res.resolvable || !res.canvas_font_url) {
                        setBrandFontNote(`"${bare}" isn't available for overlays — using Inter.`);
                        setFontFamily('inter');
                        return;
                    }
                    const spec: OverlayFontSpec = {
                        family: `AIME-Brand-${brandFontSlug(bare)}`,
                        weight: 'bold',
                        style,
                        url: res.canvas_font_url,
                        format: (res.canvas_format as OverlayFontSpec['format']) || 'woff2',
                    };
                    await ensureOverlayFontLoaded(spec);
                    if (!cancelled) { setBrandFontNote(null); renderCanvas(); }
                } catch {
                    if (!cancelled) {
                        setBrandFontNote(`"${bare}" isn't available for overlays — using Inter.`);
                        setFontFamily('inter');
                    }
                }
            })();
            return () => { cancelled = true; };
        }

        // Bundled path. Explicit default (IMP-4) — fontFamily is now free-text string.
        setBrandFontNote(null);
        const family =
            fontFamily === 'inter'      ? 'AIME-Inter' :
            fontFamily === 'playfair'   ? 'AIME-PlayfairDisplay' :
            fontFamily === 'montserrat' ? 'AIME-Montserrat' :
            fontFamily === 'bebas'      ? 'AIME-BebasNeue' :
            fontFamily === 'poppins'    ? 'AIME-Poppins' :
            fontFamily === 'caveat'     ? 'AIME-Caveat' :
                                           'AIME-Inter';
        const spec = OVERLAY_FONT_SPECS.find(s => s.family === family && s.style === style);
        if (!spec) return;
        ensureOverlayFontLoaded(spec).then(() => {
            if (!cancelled) renderCanvas();
        });
        return () => { cancelled = true; };
    }, [fontFamily, italic, renderCanvas, projectId]);

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        // When logo_fetch_failed=true, isSubmitting is reset inside the 2 s
        // setTimeout (so controls stay locked while the warning is visible).
        // This flag tells the finally block to skip the reset in that case.
        let deferredClose = false;
        try {
            // ─ Re-edit data integrity ─────────────────────────────────────────
            // Always composite against the ORIGINAL pre-text image. On re-edit,
            // initialOverlayState.base_image_url holds the original URL that was
            // used for the first composite. On first composite, baseImageUrl IS
            // the original.
            const compositeAgainst = initialOverlayState?.base_image_url ?? baseImageUrl;
            const originalBaseUrl = compositeAgainst;
            // ──────────────────────────────────────────────────────────────────

            const response = await compositeText({
                base_image_url: compositeAgainst,
                text,
                size,
                text_color: textColor,
                glow_enabled: glowEnabled,
                glow_color: glowColor,
                shadow_enabled: shadowEnabled,
                shadow_color: shadowColor,
                position,
                font_family: fontFamily,
                italic,
                fallback_to_prompt: fallbackToPrompt,
                ...(fallbackToPrompt
                    ? { fallback_visual_prompt: fallbackPromptText }
                    : {}),
                ...(projectId ? { project_id: projectId } : {}),
                // Logo fields — use resolvedLogoUrl (accounts for brand vs upload source).
                // logoEnabled=false → send null so backend skips the logo layer entirely.
                logo_url: resolvedLogoUrl,
                logo_size: logoSize,
                logo_position: logoPosition,
                logo_opacity: logoOpacity,
                // logoSource is already in wire format ('brand_guide' | 'upload') — no translation needed.
                logo_source: logoSource,
                logo_shadow_enabled: logoShadowEnabled,
                logo_shadow_color: logoShadowColor,
                logo_glow_enabled: logoGlowEnabled,
                logo_glow_color: logoGlowColor,
            });

            // Stamp base_image_url into the overlay_state handed back to the caller.
            // The backend deliberately omits base_image_url from the echoed overlay_state
            // (see OverlayStateSchema); the frontend is the source of truth for the original URL.
            const augmentedOverlayState = {
                ...response.overlay_state,
                base_image_url: originalBaseUrl,
            };

            if (response.logo_fetch_failed) {
                // Show warning BEFORE closing — keep isSubmitting=true so controls
                // stay locked during the 2 s visibility window, then reset + close.
                deferredClose = true;
                setLogoWarning('Logo could not be loaded and was skipped.');
                onApplied({ ...response, overlay_state: augmentedOverlayState });
                setTimeout(() => { setIsSubmitting(false); onClose(); }, 2000);
                return;
            }

            onApplied({ ...response, overlay_state: augmentedOverlayState });
            onClose();
        } catch (err: any) {
            setError(getApiError(err, 'Failed to apply text overlay.'));
        } finally {
            // The logo_fetch_failed path sets deferredClose=true and manages
            // isSubmitting itself (reset inside the 2 s setTimeout). Skip here.
            if (!deferredClose) {
                setIsSubmitting(false);
            }
        }
    };

    // ── "Generate All Ratios" — aspectVariantsEnabled only ────────────────────
    // Shared fields (text/font/colours/logo identity) come from the live
    // state; PER-RATIO layout fields come from ratioLayouts. 1:1 goes through
    // the same compositeText + onApplied (handleOverlayApplied) path as
    // today's single-overlay flow — same endpoint, same payload shape.
    // OverlayState shape (no base_image_url -- that's only known once a crop
    // exists) for persisting a ratio's LAYOUT ahead of the crop/composite
    // steps, so a mid-run failure still leaves the user's position/size/logo
    // choices visible and reusable on retry, and _variant_needs_regenerate
    // (server) correctly sees "configured but not yet composited" rather
    // than "never configured".
    const assembleOverlayStateFor = useCallback((layout: RatioLayoutSlot): OverlayState => ({
        text,
        size: layout.size,
        text_color: textColor,
        glow_enabled: glowEnabled,
        glow_color: glowColor,
        shadow_enabled: shadowEnabled,
        shadow_color: shadowColor,
        position: layout.position,
        font_family: fontFamily,
        italic,
        fallback_to_prompt: fallbackToPrompt,
        logo_url: resolvedLogoUrl,
        logo_size: layout.logoSize,
        logo_position: layout.logoPosition,
        logo_opacity: logoOpacity,
        logo_source: logoSource,
        logo_shadow_enabled: logoShadowEnabled,
        logo_shadow_color: logoShadowColor,
        logo_glow_enabled: logoGlowEnabled,
        logo_glow_color: logoGlowColor,
    }), [text, textColor, glowEnabled, glowColor, shadowEnabled, shadowColor, fontFamily, italic,
        fallbackToPrompt, resolvedLogoUrl, logoOpacity, logoSource,
        logoShadowEnabled, logoShadowColor, logoGlowEnabled, logoGlowColor]);

    const buildOverlayPayloadFor = useCallback((layout: RatioLayoutSlot, baseUrl: string) => ({
        base_image_url: baseUrl,
        text,
        size: layout.size,
        text_color: textColor,
        glow_enabled: glowEnabled,
        glow_color: glowColor,
        shadow_enabled: shadowEnabled,
        shadow_color: shadowColor,
        position: layout.position,
        font_family: fontFamily,
        italic,
        fallback_to_prompt: fallbackToPrompt,
        ...(fallbackToPrompt ? { fallback_visual_prompt: fallbackPromptText } : {}),
        ...(projectId ? { project_id: projectId } : {}),
        logo_url: resolvedLogoUrl,
        logo_size: layout.logoSize,
        logo_position: layout.logoPosition,
        logo_opacity: logoOpacity,
        logo_source: logoSource,
        logo_shadow_enabled: logoShadowEnabled,
        logo_shadow_color: logoShadowColor,
        logo_glow_enabled: logoGlowEnabled,
        logo_glow_color: logoGlowColor,
    }), [text, textColor, glowEnabled, glowColor, shadowEnabled, shadowColor, fontFamily, italic,
        fallbackToPrompt, fallbackPromptText, projectId, resolvedLogoUrl, logoOpacity, logoSource,
        logoShadowEnabled, logoShadowColor, logoGlowEnabled, logoGlowColor]);

    const handleGenerateAllRatios = async () => {
        if (!savedAdId) return;
        setIsSubmitting(true);
        setError(null);
        setRatioErrors({});
        const failedRatios: string[] = [];
        try {
            // Sync the currently-active ratio's live working values into
            // ratioLayouts first, so every ratio reads its latest values below,
            // regardless of which tab happened to be open when Generate was clicked.
            const syncedLayouts: Record<AdImageRatioKey, RatioLayoutSlot> = {
                ...ratioLayouts,
                [activeRatio]: { ...ratioLayouts[activeRatio], position, size, logoPosition, logoSize },
            };
            setRatioLayouts(syncedLayouts);

            const compositeAgainst = initialOverlayState?.base_image_url ?? baseImageUrl;

            // 1. Square (1:1) FIRST — the primary image every other consumer
            //    reads (gallery card, resolve_clean_base, Meta cover image),
            //    so it must land before anything that could fail.
            setGeneratingStep(`Generating ${AD_ASPECT_RATIOS.square.ratioLabel}…`);
            const squareResponse = await compositeText(buildOverlayPayloadFor(syncedLayouts.square, compositeAgainst));
            const augmentedSquareState = { ...squareResponse.overlay_state, base_image_url: compositeAgainst };
            if (squareResponse.logo_fetch_failed) {
                setLogoWarning('Logo could not be loaded and was skipped.');
            }
            onApplied({ ...squareResponse, overlay_state: augmentedSquareState });

            // 2. Derived ratios, in order. One failing must not abort the other
            //    or leave the already-committed 1:1 in doubt.
            const newVariantResults: Partial<Record<'vertical' | 'horizontal', AdImageVariant>> = { ...variantResults };
            for (const ratio of DERIVED_AD_RATIOS) {
                const layout = syncedLayouts[ratio];
                try {
                    setGeneratingStep(`Cropping ${AD_ASPECT_RATIOS[ratio].ratioLabel}…`);
                    await putAdImageVariant(savedAdId, ratio, {
                        pan_x: layout.panX, pan_y: layout.panY,
                        overlay_state: assembleOverlayStateFor(layout),
                    });
                    const cropResult = await cropAdImageVariant(savedAdId, ratio);
                    if (!cropResult.available || !cropResult.crop_url) {
                        failedRatios.push(ratio);
                        setRatioErrors(prev => ({ ...prev, [ratio]: `Couldn't create the ${AD_ASPECT_RATIOS[ratio].ratioLabel} crop — try again.` }));
                        continue;
                    }
                    setGeneratingStep(`Compositing ${AD_ASPECT_RATIOS[ratio].ratioLabel}…`);
                    const overlayResponse = await compositeText(buildOverlayPayloadFor(layout, cropResult.crop_url));
                    const augmentedState = { ...overlayResponse.overlay_state, base_image_url: cropResult.crop_url };
                    const saved = await putAdImageVariant(savedAdId, ratio, {
                        variant_image_url: overlayResponse.url,
                        thumbnail_url: overlayResponse.thumbnail_url ?? null,
                        width: overlayResponse.width,
                        height: overlayResponse.height,
                        overlay_state: augmentedState,
                    });
                    newVariantResults[ratio] = saved;
                } catch (err: any) {
                    failedRatios.push(ratio);
                    setRatioErrors(prev => ({ ...prev, [ratio]: getApiError(err, `Failed to generate ${AD_ASPECT_RATIOS[ratio].ratioLabel}.`) }));
                }
            }
            setVariantResults(newVariantResults);
            onVariantsSaved?.(Object.values(newVariantResults).filter((v): v is AdImageVariant => !!v));
            setGeneratingStep(null);
            // Only auto-close on a fully clean run — a ratio failure keeps the
            // modal open so the error (and Retry-by-clicking-Generate-again) is visible.
            if (failedRatios.length === 0) {
                onClose();
            }
        } catch (err: any) {
            setError(getApiError(err, 'Failed to generate images.'));
            setGeneratingStep(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Button label logic ────────────────────────────────────────────────────
    // "Generate" when this is a first composite (preview still equals the bare
    // base image) OR when the fallback AI path is chosen.
    // "Apply" for a re-composite of an already-composited image.
    const isFirstComposite = previewUrl === baseImageUrl;
    const submitLabel = isFirstComposite || fallbackToPrompt ? 'Generate' : 'Apply';

    const hasFallbackPrompt = (fallbackVisualPrompt ?? '').trim().length > 0;

    const charCount = text.length;
    const overLimit = charCount > 200;
    // Disable submit when logo is enabled but no URL has been resolved yet
    // (e.g. brand source with no brand guide logo, or upload tab with no upload yet).
    const logoBlocked = logoEnabled && !resolvedLogoUrl;

    return (
        <>
            <div
                className="asset-editor-overlay"
                onMouseDown={handleOverlayMouseDown}
                onClick={handleOverlayClick}
            >
                <div className="asset-editor-modal text-overlay-modal" role="dialog" aria-modal="true" aria-label="Add Overlay">
                    {/* ── Header ── */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0 }}>Add Overlay</h3>
                        <button
                            onClick={isSubmitting ? undefined : onClose}
                            disabled={isSubmitting}
                            aria-label="Close"
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                color: 'var(--text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                opacity: isSubmitting ? 0.4 : 1,
                            }}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* ── Body ── */}
                    <div className="text-overlay-modal-body">
                        {/* Left: live canvas preview */}
                        <div className="text-overlay-preview">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
                            {aspectVariantsEnabled && (
                                <div className="overlay-tab-strip" role="tablist" aria-label="Ad shape">
                                    {(Object.keys(AD_ASPECT_RATIOS) as AdImageRatioKey[]).map(r => (
                                        <button
                                            key={r}
                                            type="button"
                                            role="tab"
                                            aria-selected={activeRatio === r}
                                            className={`overlay-tab${activeRatio === r ? ' active' : ''}`}
                                            onClick={() => handleRatioTabChange(r)}
                                            disabled={isSubmitting || !!generatingStep}
                                            title={AD_ASPECT_RATIOS[r].note}
                                        >
                                            {AD_ASPECT_RATIOS[r].ratioLabel}
                                            {r !== 'square' && (variantResults[r as 'vertical' | 'horizontal']?.variant_image_url) ? ' ✓' : ''}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {aspectVariantsEnabled && activeRatio !== 'square' && baseNaturalSize ? (() => {
                                // Combined pane: PanCropViewport owns sizing/drag INTERACTION,
                                // the live text-overlay canvas is passed as its `children`
                                // overlay — one visual box instead of two stacked ones (a
                                // pan-only image preview above a separate, previously
                                // fixed-max-width-and-therefore-overflow-prone canvas below).
                                const dims = targetDimsFor(activeRatio);
                                const axis = panAxisFor(baseNaturalSize.w, baseNaturalSize.h, dims.width, dims.height);
                                return (
                                <>
                                <PanCropViewport
                                    imageUrl={initialOverlayState?.base_image_url ?? baseImageUrl}
                                    srcWidth={baseNaturalSize.w}
                                    srcHeight={baseNaturalSize.h}
                                    targetWidth={dims.width}
                                    targetHeight={dims.height}
                                    panX={ratioLayouts[activeRatio].panX}
                                    panY={ratioLayouts[activeRatio].panY}
                                    panAxis={axis}
                                    onPanChange={(x, y) => handlePanChange(activeRatio, x, y)}
                                    disabled={isSubmitting || !!generatingStep}
                                    ariaLabelRatio={AD_ASPECT_RATIOS[activeRatio].ratioLabel}
                                >
                                    <canvas ref={canvasRef} aria-label="Live preview" />
                                </PanCropViewport>
                                {axis !== 'none' && (
                                    <button
                                        type="button"
                                        className="ac-download-btn"
                                        onClick={() => handlePanChange(activeRatio, 0.5, 0.5)}
                                        disabled={isSubmitting || !!generatingStep}
                                        style={{ marginTop: 6 }}
                                    >
                                        Centre
                                    </button>
                                )}
                                </>
                                );
                            })() : (
                                // Square tab (or aspectVariantsEnabled=false, or base image not
                                // yet loaded) — no PanCropViewport involved, standalone canvas
                                // exactly as before.
                                <canvas ref={canvasRef} aria-label="Live preview" />
                            )}
                            {aspectVariantsEnabled && activeRatio !== 'square' && (() => {
                                const dims = targetDimsFor(activeRatio);
                                const upscale = baseNaturalSize
                                    ? dims.width / cropWindowSize(baseNaturalSize.w, baseNaturalSize.h, dims.width, dims.height)[0]
                                    : 1;
                                return upscale > 1.5 ? (
                                    <p className="pan-crop-upscale-note">
                                        Upscaled {upscale.toFixed(1)}× from the original — softer than the square.
                                    </p>
                                ) : null;
                            })()}
                            {ratioErrors[activeRatio] && (
                                <p className="pan-crop-upscale-note">{ratioErrors[activeRatio]}</p>
                            )}
                        </div>
                        </div>

                        {/* Right: form controls */}
                        <div className="text-overlay-controls">
                            {/* ── Tab strip ── */}
                            <div className="overlay-tab-strip" role="tablist" aria-label="Overlay layers">
                                <button
                                    className={`overlay-tab${activeTab === 'text' ? ' active' : ''}`}
                                    onClick={() => setActiveTab('text')}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'text'}
                                    disabled={isSubmitting}
                                >Text</button>
                                <button
                                    className={`overlay-tab${activeTab === 'logo' ? ' active' : ''}`}
                                    onClick={() => setActiveTab('logo')}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'logo'}
                                    disabled={isSubmitting}
                                >Logo</button>
                            </div>

                            {/* ── Logo tab ── */}
                            {activeTab === 'logo' && (
                                <>
                                    {/* Show logo toggle */}
                                    <div className="text-overlay-field">
                                        <label className="text-overlay-check-row">
                                            <input
                                                type="checkbox"
                                                checked={logoEnabled}
                                                onChange={e => setLogoEnabled(e.target.checked)}
                                                disabled={isSubmitting}
                                            />
                                            Show logo
                                        </label>
                                    </div>

                                    {logoEnabled && (
                                        <>
                                            {/* Logo source */}
                                            <div className="text-overlay-field">
                                                <span className="text-overlay-field-label">Logo source</span>
                                                <div className="overlay-logo-source" role="radiogroup" aria-label="Logo source">
                                                    <button
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={logoSource === 'brand_guide'}
                                                        className={`overlay-logo-source-btn${logoSource === 'brand_guide' ? ' active' : ''}`}
                                                        onClick={() => setLogoSource('brand_guide')}
                                                        disabled={isSubmitting}
                                                    >Brand Guide</button>
                                                    <button
                                                        type="button"
                                                        role="radio"
                                                        aria-checked={logoSource === 'upload'}
                                                        className={`overlay-logo-source-btn${logoSource === 'upload' ? ' active' : ''}`}
                                                        onClick={() => setLogoSource('upload')}
                                                        disabled={isSubmitting}
                                                    >Upload</button>
                                                </div>
                                            </div>

                                            {/* Brand guide preview / upload */}
                                            {logoSource === 'brand_guide' ? (
                                                <>
                                                    {/* Variant dropdown — only shown when 2+ variants are available.
                                                        Single-option selects are noisy; hide when there's nothing to choose. */}
                                                    {availableLogoVariants.length > 1 && (
                                                        <div className="text-overlay-field">
                                                            <label className="text-overlay-field-label" htmlFor="logo-variant">
                                                                Logo variant
                                                            </label>
                                                            <select
                                                                id="logo-variant"
                                                                className="text-overlay-select"
                                                                value={selectedLogoVariant ?? ''}
                                                                onChange={e => setSelectedLogoVariant(e.target.value as LogoVariantKey)}
                                                                disabled={isSubmitting}
                                                                aria-label="Brand logo variant"
                                                            >
                                                                {availableLogoVariants.map(v => (
                                                                    <option key={v.key} value={v.key}>{v.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                    <div className="text-overlay-field">
                                                        {brandLogoUrl
                                                            ? (
                                                                <img
                                                                    src={brandLogoUrl}
                                                                    alt={
                                                                        selectedLogoVariant
                                                                            ? `Brand logo (${
                                                                                availableLogoVariants.find(v => v.key === selectedLogoVariant)?.label
                                                                                ?? 'selected variant'
                                                                            })`
                                                                            : 'Brand logo'
                                                                    }
                                                                    className="overlay-logo-preview overlay-logo-variant-preview"
                                                                />
                                                            )
                                                            : <span className="text-overlay-field-label">No brand logo found</span>
                                                        }
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-overlay-field">
                                                    <button
                                                        type="button"
                                                        className="text-overlay-select"
                                                        onClick={() => logoFileRef.current?.click()}
                                                        disabled={isSubmitting || isUploadingLogo}
                                                        style={{ cursor: 'pointer', textAlign: 'left' }}
                                                    >
                                                        {isUploadingLogo ? 'Uploading…' : (uploadedLogoUrl ? 'Replace logo…' : 'Upload logo…')}
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={logoFileRef}
                                                        accept="image/png,image/jpeg,image/webp,image/gif"
                                                        onChange={handleLogoUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {uploadedLogoUrl && (
                                                        <img src={uploadedLogoUrl} alt="Uploaded logo" className="overlay-logo-preview" style={{ marginTop: 6 }} />
                                                    )}
                                                    {logoLoadError && (
                                                        <div className="text-overlay-error" style={{ marginTop: 4 }}>{logoLoadError}</div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Logo size */}
                                            <div className="text-overlay-field">
                                                <label className="text-overlay-field-label" htmlFor="logo-size">Logo size</label>
                                                <select
                                                    id="logo-size"
                                                    className="text-overlay-select"
                                                    value={logoSize}
                                                    onChange={e => setLogoSize(e.target.value as 'small' | 'medium' | 'large' | 'xlarge')}
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="small">Small</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="large">Large</option>
                                                    <option value="xlarge">Extra Large</option>
                                                </select>
                                            </div>

                                            {/* Logo opacity */}
                                            <div className="text-overlay-field">
                                                <label className="text-overlay-field-label" htmlFor="logo-opacity">Logo opacity</label>
                                                <select
                                                    id="logo-opacity"
                                                    className="text-overlay-select"
                                                    value={String(logoOpacity)}
                                                    onChange={e => setLogoOpacity(parseFloat(e.target.value))}
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="1">100%</option>
                                                    <option value="0.75">75%</option>
                                                    <option value="0.5">50%</option>
                                                </select>
                                            </div>

                                            {/* Logo drop shadow */}
                                            <div className="text-overlay-field">
                                                <label className="text-overlay-check-row">
                                                    <input
                                                        type="checkbox"
                                                        checked={logoShadowEnabled}
                                                        onChange={e => setLogoShadowEnabled(e.target.checked)}
                                                        disabled={isSubmitting}
                                                    />
                                                    Logo drop shadow
                                                </label>
                                                {logoShadowEnabled && (
                                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <label className="text-overlay-field-label">Shadow colour</label>
                                                        <select
                                                            className="text-overlay-select"
                                                            value={logoShadowColorPreset}
                                                            onChange={handleLogoShadowPresetChange}
                                                            disabled={isSubmitting}
                                                        >
                                                            <option value="white">White</option>
                                                            <option value="black">Black</option>
                                                            {brandPrimary && <option value="brand_primary">Brand Primary</option>}
                                                            {brandSecondary && <option value="brand_secondary">Brand Secondary</option>}
                                                            <option value="custom">Custom…</option>
                                                        </select>
                                                        {logoShadowColorPreset === 'custom' && (
                                                            <div className="text-overlay-color-pair" style={{ marginTop: 6 }}>
                                                                <input
                                                                    type="color"
                                                                    value={logoShadowColorHex}
                                                                    onChange={e => {
                                                                        setLogoShadowColorHex(e.target.value);
                                                                    }}
                                                                    disabled={isSubmitting}
                                                                    aria-label="Logo shadow colour picker"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={logoShadowColorHex}
                                                                    onChange={e => setLogoShadowColorHex(e.target.value)}
                                                                    onBlur={e => setLogoShadowColorHex(sanitiseHex(e.target.value, logoShadowColorHex))}
                                                                    disabled={isSubmitting}
                                                                    maxLength={7}
                                                                    aria-label="Logo shadow colour hex value"
                                                                    placeholder="#000000"
                                                                />
                                                            </div>
                                                        )}
                                                        {logoShadowColorPreset !== 'custom' && (
                                                            <div className="text-overlay-swatch" style={{ background: logoShadowColor }} aria-hidden="true" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Logo glow */}
                                            <div className="text-overlay-field">
                                                <label className="text-overlay-check-row">
                                                    <input
                                                        type="checkbox"
                                                        checked={logoGlowEnabled}
                                                        onChange={e => setLogoGlowEnabled(e.target.checked)}
                                                        disabled={isSubmitting}
                                                    />
                                                    Logo glow
                                                </label>
                                                {logoGlowEnabled && (
                                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        <label className="text-overlay-field-label">Glow colour</label>
                                                        <select
                                                            className="text-overlay-select"
                                                            value={logoGlowColorPreset}
                                                            onChange={handleLogoGlowPresetChange}
                                                            disabled={isSubmitting}
                                                        >
                                                            <option value="white">White</option>
                                                            <option value="black">Black</option>
                                                            {brandPrimary && <option value="brand_primary">Brand Primary</option>}
                                                            {brandSecondary && <option value="brand_secondary">Brand Secondary</option>}
                                                            <option value="custom">Custom…</option>
                                                        </select>
                                                        {logoGlowColorPreset === 'custom' && (
                                                            <div className="text-overlay-color-pair" style={{ marginTop: 6 }}>
                                                                <input
                                                                    type="color"
                                                                    value={logoGlowColorHex}
                                                                    onChange={e => {
                                                                        setLogoGlowColorHex(e.target.value);
                                                                    }}
                                                                    disabled={isSubmitting}
                                                                    aria-label="Logo glow colour picker"
                                                                />
                                                                <input
                                                                    type="text"
                                                                    value={logoGlowColorHex}
                                                                    onChange={e => setLogoGlowColorHex(e.target.value)}
                                                                    onBlur={e => setLogoGlowColorHex(sanitiseHex(e.target.value, logoGlowColorHex))}
                                                                    disabled={isSubmitting}
                                                                    maxLength={7}
                                                                    aria-label="Logo glow colour hex value"
                                                                    placeholder="#000000"
                                                                />
                                                            </div>
                                                        )}
                                                        {logoGlowColorPreset !== 'custom' && (
                                                            <div className="text-overlay-swatch" style={{ background: logoGlowColor }} aria-hidden="true" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Logo position grid */}
                                            <div className="text-overlay-field">
                                                <span className="text-overlay-field-label">Logo position</span>
                                                <div className="position-grid" role="group" aria-label="Logo position">
                                                    {Array.from({ length: 9 }, (_, i) => (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            className={`position-cell${logoPosition === i ? ' active' : ''}`}
                                                            onClick={() => setLogoPosition(i)}
                                                            disabled={isSubmitting}
                                                            aria-label={POSITION_LABELS[i] + ' position'}
                                                            aria-pressed={logoPosition === i}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* ── Text tab ── */}
                            {activeTab === 'text' && (<>
                            {/* Overlay text */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-field-label" htmlFor="overlay-text">
                                    Overlay text
                                </label>
                                <textarea
                                    id="overlay-text"
                                    className="text-overlay-textarea"
                                    value={text}
                                    onChange={e => setText(e.target.value)}
                                    maxLength={200}
                                    rows={1}
                                    placeholder="e.g. 50% OFF THIS WEEK"
                                    disabled={isSubmitting}
                                />
                                <span className={`text-overlay-char-counter${overLimit ? ' over-limit' : ''}`}>
                                    {charCount} / 200
                                </span>
                            </div>

                            {/* Font family */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-field-label" htmlFor="overlay-font">Font</label>
                                <select
                                    id="overlay-font"
                                    className="text-overlay-select"
                                    value={fontFamily}
                                    onChange={e => setFontFamily(e.target.value)}
                                    disabled={isSubmitting}
                                >
                                    {brandFontOptions.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                    <option value="inter">Inter (Modern)</option>
                                    <option value="playfair">Playfair Display (Elegant)</option>
                                    <option value="montserrat">Montserrat (Geometric)</option>
                                    <option value="bebas">Bebas Neue (Condensed Display)</option>
                                    <option value="poppins">Poppins (Friendly Sans)</option>
                                    <option value="caveat">Caveat (Handwritten)</option>
                                </select>
                                {brandFontNote && (
                                    <p className="text-overlay-field-note">{brandFontNote}</p>
                                )}
                            </div>

                            {/* Italic */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-check-row">
                                    <input
                                        type="checkbox"
                                        checked={italic}
                                        onChange={e => setItalic(e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    Italic
                                </label>
                            </div>

                            {/* Size */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-field-label" htmlFor="overlay-size">
                                    Size
                                </label>
                                <select
                                    id="overlay-size"
                                    className="text-overlay-select"
                                    value={size}
                                    onChange={e => setSize(e.target.value as OverlayState['size'])}
                                    disabled={isSubmitting}
                                >
                                    <option value="small">Small</option>
                                    <option value="medium">Medium</option>
                                    <option value="large">Large</option>
                                </select>
                            </div>

                            {/* Text colour */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-field-label">Text colour</label>
                                <select
                                    className="text-overlay-select"
                                    value={textColorPreset}
                                    onChange={handleTextPresetChange}
                                    disabled={isSubmitting}
                                >
                                    <option value="white">White</option>
                                    <option value="black">Black</option>
                                    {brandPrimary && <option value="brand_primary">Brand Primary</option>}
                                    {brandSecondary && <option value="brand_secondary">Brand Secondary</option>}
                                    <option value="custom">Custom…</option>
                                </select>
                                {textColorPreset === 'custom' && (
                                    <div className="text-overlay-color-pair" style={{ marginTop: 6 }}>
                                        <input
                                            type="color"
                                            value={textColorHex}
                                            onChange={e => {
                                                setTextColorHex(e.target.value);
                                            }}
                                            disabled={isSubmitting}
                                            aria-label="Text colour picker"
                                        />
                                        <input
                                            type="text"
                                            value={textColorHex}
                                            onChange={e => setTextColorHex(e.target.value)}
                                            onBlur={e => setTextColorHex(sanitiseHex(e.target.value, textColorHex))}
                                            disabled={isSubmitting}
                                            maxLength={7}
                                            aria-label="Text colour hex value"
                                            placeholder="#FFFFFF"
                                        />
                                    </div>
                                )}
                                {textColorPreset !== 'custom' && (
                                    <div className="text-overlay-swatch" style={{ background: textColor }} aria-hidden="true" />
                                )}
                            </div>

                            {/* Glow */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-check-row">
                                    <input
                                        type="checkbox"
                                        checked={glowEnabled}
                                        onChange={e => setGlowEnabled(e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    Enable glow
                                </label>
                                {glowEnabled && (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <label className="text-overlay-field-label">Glow colour</label>
                                        <select
                                            className="text-overlay-select"
                                            value={glowColorPreset}
                                            onChange={handleGlowPresetChange}
                                            disabled={isSubmitting}
                                        >
                                            <option value="white">White</option>
                                            <option value="black">Black</option>
                                            {brandPrimary && <option value="brand_primary">Brand Primary</option>}
                                            {brandSecondary && <option value="brand_secondary">Brand Secondary</option>}
                                            <option value="custom">Custom…</option>
                                        </select>
                                        {glowColorPreset === 'custom' && (
                                            <div className="text-overlay-color-pair" style={{ marginTop: 6 }}>
                                                <input
                                                    type="color"
                                                    value={glowColorHex}
                                                    onChange={e => {
                                                        setGlowColorHex(e.target.value);
                                                    }}
                                                    disabled={isSubmitting}
                                                    aria-label="Glow colour picker"
                                                />
                                                <input
                                                    type="text"
                                                    value={glowColorHex}
                                                    onChange={e => setGlowColorHex(e.target.value)}
                                                    onBlur={e => setGlowColorHex(sanitiseHex(e.target.value, glowColorHex))}
                                                    disabled={isSubmitting}
                                                    maxLength={7}
                                                    aria-label="Glow colour hex value"
                                                    placeholder="#000000"
                                                />
                                            </div>
                                        )}
                                        {glowColorPreset !== 'custom' && (
                                            <div className="text-overlay-swatch" style={{ background: glowColor }} aria-hidden="true" />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Drop shadow */}
                            <div className="text-overlay-field">
                                <label className="text-overlay-check-row">
                                    <input
                                        type="checkbox"
                                        checked={shadowEnabled}
                                        onChange={e => setShadowEnabled(e.target.checked)}
                                        disabled={isSubmitting}
                                    />
                                    Drop shadow
                                </label>
                                {shadowEnabled && (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <label className="text-overlay-field-label">Shadow colour</label>
                                        <select
                                            className="text-overlay-select"
                                            value={shadowColorPreset}
                                            onChange={handleShadowPresetChange}
                                            disabled={isSubmitting}
                                        >
                                            <option value="white">White</option>
                                            <option value="black">Black</option>
                                            {brandPrimary && <option value="brand_primary">Brand Primary</option>}
                                            {brandSecondary && <option value="brand_secondary">Brand Secondary</option>}
                                            <option value="custom">Custom…</option>
                                        </select>
                                        {shadowColorPreset === 'custom' && (
                                            <div className="text-overlay-color-pair" style={{ marginTop: 6 }}>
                                                <input
                                                    type="color"
                                                    value={shadowColorHex}
                                                    onChange={e => {
                                                        setShadowColorHex(e.target.value);
                                                    }}
                                                    disabled={isSubmitting}
                                                    aria-label="Shadow colour picker"
                                                />
                                                <input
                                                    type="text"
                                                    value={shadowColorHex}
                                                    onChange={e => setShadowColorHex(e.target.value)}
                                                    onBlur={e => setShadowColorHex(sanitiseHex(e.target.value, shadowColorHex))}
                                                    disabled={isSubmitting}
                                                    maxLength={7}
                                                    aria-label="Shadow colour hex value"
                                                    placeholder="#000000"
                                                />
                                            </div>
                                        )}
                                        {shadowColorPreset !== 'custom' && (
                                            <div className="text-overlay-swatch" style={{ background: shadowColor }} aria-hidden="true" />
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* 3×3 position grid */}
                            <div className="text-overlay-field">
                                <span className="text-overlay-field-label">Position</span>
                                <div className="position-grid" role="group" aria-label="Text position">
                                    {Array.from({ length: 9 }, (_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`position-cell${position === i ? ' active' : ''}`}
                                            onClick={() => setPosition(i)}
                                            disabled={isSubmitting}
                                            aria-label={POSITION_LABELS[i] + ' position'}
                                            aria-pressed={position === i}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Fallback option */}
                            <div className="text-overlay-fallback-section">
                                <label
                                    className="text-overlay-check-row"
                                    title={
                                        !hasFallbackPrompt
                                            ? 'No original prompt available — use the PIL compositor (default) or regenerate the image first'
                                            : undefined
                                    }
                                >
                                    <input
                                        type="checkbox"
                                        checked={fallbackToPrompt}
                                        onChange={e => setFallbackToPrompt(e.target.checked)}
                                        disabled={isSubmitting || !hasFallbackPrompt}
                                    />
                                    Render as part of artwork (AI-painted, less precise)
                                </label>
                                {fallbackToPrompt && (
                                    <div className="text-overlay-field">
                                        <label className="text-overlay-field-label" htmlFor="fallback-prompt">
                                            Visual description (without text)
                                        </label>
                                        <textarea
                                            id="fallback-prompt"
                                            className="text-overlay-textarea"
                                            value={fallbackPromptText}
                                            onChange={e => setFallbackPromptText(e.target.value)}
                                            rows={3}
                                            placeholder="Describe the image visuals (no text)"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                )}
                            </div>
                            </>)}
                            {/* ── Logo warning (shown after submit if logo skipped) ── */}
                            {logoWarning && (
                                <div className="overlay-logo-warning">{logoWarning}</div>
                            )}
                        </div>
                    </div>

                    {/* ── Error ── */}
                    {error && <div className="text-overlay-error">{error}</div>}

                    {/* ── Footer ── */}
                    <div className="text-overlay-footer">
                        <GlassButton
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={aspectVariantsEnabled ? handleGenerateAllRatios : handleSubmit}
                            disabled={isSubmitting || overLimit || logoBlocked}
                            title={logoBlocked ? 'Pick a logo source or disable Show logo' : undefined}
                        >
                            {aspectVariantsEnabled
                                ? (isSubmitting ? (generatingStep || 'Generating…') : 'Generate All Ratios')
                                : (isSubmitting
                                    ? (fallbackToPrompt ? 'Generating with AI…' : 'Applying…')
                                    : submitLabel)}
                        </GlassButton>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TextOverlayModal;
